import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Form, Input, Switch, message } from "antd";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { generateCodeFromName } from "../../../utils/codeUtils";

const { TextArea } = Input;

const CategoryModal = ({ visible, onCancel, onSuccess, category }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && category) {
      // Edit mode - populate form
      form.setFieldsValue({
        name: category.name,
        description: category.description || "",
        is_active: category.is_active === 1,
      });
    } else if (visible) {
      // Create mode - reset form
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
  }, [visible, category, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const normalizedName = values.name?.trim() || "";
      const shouldKeepExistingCode =
        category && normalizedName === (category.name || "").trim();
      const generatedCode = generateCodeFromName(normalizedName);
      const finalCode =
        shouldKeepExistingCode && category?.code
          ? category.code
          : generatedCode || category?.code || "";

      const payload = {
        name: values.name,
        code: finalCode,
        description: values.description || null,
        is_active: values.is_active ? 1 : 0,
      };

      const method = category ? "PUT" : "POST";
      const url = category
        ? domainSettings.urls.bot.categoryDetail.replace(":id", category.id)
        : domainSettings.urls.bot.categories;

      const response = await Utilities.fetchWithAuth(
        method,
        url,
        payload,
        navigate,
      );

      if (response.ok) {
        message.success(
          category
            ? "Category updated successfully"
            : "Category created successfully",
        );
        form.resetFields();
        onSuccess();
      } else {
        Utilities.showError(response, "Failed to save category");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={category ? "Edit Category" : "Create Category"}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_active: true }}
      >
        <Form.Item
          label="Category Name"
          name="name"
          rules={[
            { required: true, message: "Please enter category name" },
            { max: 200, message: "Name cannot exceed 200 characters" },
          ]}
        >
          <Input placeholder="e.g., Uniforms, Books, Supplies" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea
            rows={3}
            placeholder="Optional description of this category"
          />
        </Form.Item>

        <Form.Item label="Active" name="is_active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CategoryModal;
